import React, { useState } from 'react';
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Badge,
    Button,
    HStack,
    VStack,
    Text,
    useColorModeValue,
    Checkbox,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    Card,
    CardBody,
    Skeleton,
    Tooltip,
    Code,
} from '@chakra-ui/react';
import {
    FiCheck,
    FiX,
    FiMoreVertical,
    FiEye,
    FiCalendar,
    FiSmartphone,
} from 'react-icons/fi';
import { LoginRequest } from '../types';
import { formatDistanceToNow, format } from 'date-fns';
import { id } from 'date-fns/locale';

interface LoginRequestTableProps {
    requests: LoginRequest[];
    loading?: boolean;
    onApprove: (requestId: number, approvedBy: string) => Promise<{ success: boolean; message?: string }>;
    onReject: (requestId: number, rejectedBy: string) => Promise<{ success: boolean; message?: string }>;
    selectedIds?: number[];
    onSelectionChange?: (ids: number[]) => void;
    currentUser?: string;
}

interface ActionButtonsProps {
    request: LoginRequest;
    onApprove: (requestId: number, approvedBy: string) => Promise<{ success: boolean; message?: string }>;
    onReject: (requestId: number, rejectedBy: string) => Promise<{ success: boolean; message?: string }>;
    currentUser?: string;
}

const StatusBadge: React.FC<{ status: LoginRequest['status'] }> = ({ status }) => {
    const colorScheme = {
        pending: 'yellow',
        approved: 'green',
        rejected: 'red',
    }[status];

    const label = {
        pending: 'Pending',
        approved: 'Disetujui',
        rejected: 'Ditolak',
    }[status];

    return (
        <Badge colorScheme={colorScheme} variant="subtle" borderRadius="md" px={2} py={1}>
            {label}
        </Badge>
    );
};

const ActionButtons: React.FC<ActionButtonsProps> = ({
    request,
    onApprove,
    onReject,
    currentUser = 'admin@gbs.id',
}) => {
    const [loading, setLoading] = useState(false);
    const { isOpen: isApproveOpen, onOpen: onApproveOpen, onClose: onApproveClose } = useDisclosure();
    const { isOpen: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
    const cancelRef = React.useRef<HTMLButtonElement>(null);

    const handleApprove = async () => {
        setLoading(true);
        try {
            await onApprove(request.id, currentUser);
            onApproveClose();
        } catch (error) {
            console.error('Approval error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        setLoading(true);
        try {
            await onReject(request.id, currentUser);
            onRejectClose();
        } catch (error) {
            console.error('Rejection error:', error);
        } finally {
            setLoading(false);
        }
    };

    if (request.status !== 'pending') {
        return (
            <VStack spacing={1} align="start">
                <Text fontSize="xs" color="gray.500">
                    {request.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                </Text>
                {request.approved_by && (
                    <Text fontSize="xs" color="gray.400">
                        oleh {request.approved_by}
                    </Text>
                )}
                {request.approved_at && (
                    <Text fontSize="xs" color="gray.400">
                        {formatDistanceToNow(new Date(request.approved_at), {
                            addSuffix: true,
                            locale: id
                        })}
                    </Text>
                )}
            </VStack>
        );
    }

    return (
        <>
            <HStack spacing={2}>
                <Tooltip label="Setujui Request">
                    <Button
                        size="sm"
                        colorScheme="green"
                        leftIcon={<FiCheck />}
                        onClick={onApproveOpen}
                        isLoading={loading}
                    >
                        Approve
                    </Button>
                </Tooltip>

                <Tooltip label="Tolak Request">
                    <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        leftIcon={<FiX />}
                        onClick={onRejectOpen}
                        isLoading={loading}
                    >
                        Reject
                    </Button>
                </Tooltip>

                <Menu>
                    <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        size="sm"
                        variant="ghost"
                    />
                    <MenuList>
                        <MenuItem icon={<FiEye />}>
                            View Details
                        </MenuItem>
                    </MenuList>
                </Menu>
            </HStack>

            {/* Approve Confirmation Dialog */}
            <AlertDialog
                isOpen={isApproveOpen}
                leastDestructiveRef={cancelRef}
                onClose={onApproveClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Approve Login Request
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Apakah Anda yakin ingin menyetujui request login untuk:
                            <VStack align="start" mt={3} p={3} bg="green.50" borderRadius="md">
                                <Text><strong>NIP:</strong> {request.nip}</Text>
                                <Text><strong>Device:</strong> {request.device_model}</Text>
                                <Text><strong>Device ID:</strong> <Code fontSize="xs">{request.device_id}</Code></Text>
                            </VStack>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onApproveClose}>
                                Batal
                            </Button>
                            <Button
                                colorScheme="green"
                                onClick={handleApprove}
                                ml={3}
                                isLoading={loading}
                            >
                                Ya, Setujui
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Reject Confirmation Dialog */}
            <AlertDialog
                isOpen={isRejectOpen}
                leastDestructiveRef={cancelRef}
                onClose={onRejectClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Reject Login Request
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Apakah Anda yakin ingin menolak request login untuk:
                            <VStack align="start" mt={3} p={3} bg="red.50" borderRadius="md">
                                <Text><strong>NIP:</strong> {request.nip}</Text>
                                <Text><strong>Device:</strong> {request.device_model}</Text>
                                <Text><strong>Device ID:</strong> <Code fontSize="xs">{request.device_id}</Code></Text>
                            </VStack>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onRejectClose}>
                                Batal
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleReject}
                                ml={3}
                                isLoading={loading}
                            >
                                Ya, Tolak
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

const LoadingSkeleton: React.FC = () => (
    <Tbody>
        {[1, 2, 3, 4, 5].map((id) => (
            <Tr key={`skeleton-row-${id}`}>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
            </Tr>
        ))}
    </Tbody>
);

const LoginRequestTable: React.FC<LoginRequestTableProps> = ({
    requests,
    loading = false,
    onApprove,
    onReject,
    selectedIds = [],
    onSelectionChange,
    currentUser,
}) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    const handleSelectAll = (checked: boolean) => {
        if (!onSelectionChange) return;

        if (checked) {
            const pendingIds = requests
                .filter(request => request.status === 'pending')
                .map(request => request.id);
            onSelectionChange(pendingIds);
        } else {
            onSelectionChange([]);
        }
    };

    const handleSelectOne = (requestId: number, checked: boolean) => {
        if (!onSelectionChange) return;

        if (checked) {
            onSelectionChange([...selectedIds, requestId]);
        } else {
            onSelectionChange(selectedIds.filter(id => id !== requestId));
        }
    };

    const pendingRequests = requests.filter(request => request.status === 'pending');
    const isAllSelected = pendingRequests.length > 0 &&
        pendingRequests.every(request => selectedIds.includes(request.id));
    const isIndeterminate = selectedIds.length > 0 && !isAllSelected;

    if (requests.length === 0 && !loading) {
        return (
            <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                    <VStack spacing={4} py={8}>
                        <FiCalendar size={48} color="gray.400" />
                        <VStack spacing={2}>
                            <Text fontSize="lg" fontWeight="medium" color="gray.500">
                                Tidak ada login request
                            </Text>
                            <Text fontSize="sm" color="gray.400">
                                Login request akan muncul di sini ketika user mengajukan permintaan akses
                            </Text>
                        </VStack>
                    </VStack>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
            <TableContainer>
                <Table variant="simple" size="md">
                    <Thead>
                        <Tr>
                            {onSelectionChange && (
                                <Th width="50px">
                                    <Checkbox
                                        isChecked={isAllSelected}
                                        isIndeterminate={isIndeterminate}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                    />
                                </Th>
                            )}
                            <Th>ID</Th>
                            <Th>NIP</Th>
                            <Th>Employee</Th>
                            <Th>Device Info</Th>
                            <Th>Status</Th>
                            <Th>Request Date</Th>
                            <Th>Actions</Th>
                        </Tr>
                    </Thead>

                    {loading ? (
                        <LoadingSkeleton />
                    ) : (
                        <Tbody>
                            {requests.map((request) => (
                                <Tr key={request.id}>
                                    {onSelectionChange && (
                                        <Td>
                                            {request.status === 'pending' && (
                                                <Checkbox
                                                    isChecked={selectedIds.includes(request.id)}
                                                    onChange={(e) => handleSelectOne(request.id, e.target.checked)}
                                                />
                                            )}
                                        </Td>
                                    )}

                                    <Td>
                                        <Code fontSize="sm">#{request.id}</Code>
                                    </Td>

                                    <Td>
                                        <Text fontWeight="medium" fontFamily="mono">
                                            {request.nip}
                                        </Text>
                                    </Td>

                                    <Td>
                                        <VStack align="start" spacing={1}>
                                            <Text fontWeight="medium">
                                                {request.employee_name || 'Loading...'}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                NIP: {request.nip}
                                            </Text>
                                        </VStack>
                                    </Td>

                                    <Td>
                                        <VStack align="start" spacing={1}>
                                            <HStack>
                                                <FiSmartphone size={14} />
                                                <Text fontSize="sm" fontWeight="medium">
                                                    {request.device_model}
                                                </Text>
                                            </HStack>
                                            <Code fontSize="xs" color="gray.500">
                                                {request.device_id}
                                            </Code>
                                        </VStack>
                                    </Td>

                                    <Td>
                                        <StatusBadge status={request.status} />
                                    </Td>

                                    <Td>
                                        <VStack align="start" spacing={1}>
                                            <Text fontSize="sm">
                                                {format(new Date(request.created_at), 'dd/MM/yyyy')}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {format(new Date(request.created_at), 'HH:mm')}
                                            </Text>
                                            <Text fontSize="xs" color="gray.400">
                                                {formatDistanceToNow(new Date(request.created_at), {
                                                    addSuffix: true,
                                                    locale: id
                                                })}
                                            </Text>
                                        </VStack>
                                    </Td>

                                    <Td>
                                        <ActionButtons
                                            request={request}
                                            onApprove={onApprove}
                                            onReject={onReject}
                                            currentUser={currentUser}
                                        />
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    )}
                </Table>
            </TableContainer>
        </Card>
    );
};

export default LoginRequestTable;
